import type { Data } from "@puckeditor/core";

export type LayerPosition = "before" | "after" | "inside";

export type BuilderBlock = {
  type?: string;
  props?: Record<string, unknown>;
};

export interface LayerNode {
  readonly id: string;
  readonly label: string;
  readonly type: string;
  readonly children?: readonly LayerNode[];
}

export interface BlockSummary {
  id: string;
  type: string;
}

type BlockLocation = {
  collection: "content" | "zone";
  zoneKey?: string;
  index: number;
  block: BuilderBlock;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function asBlockArray(value: unknown): BuilderBlock[] {
  return Array.isArray(value) ? value.filter(isRecord) as BuilderBlock[] : [];
}

function cloneZones(data: Data): Record<string, BuilderBlock[]> {
  if (!isRecord(data.zones)) return {};

  return Object.fromEntries(
    Object.entries(data.zones).map(([zoneKey, zoneItems]) => [zoneKey, [...asBlockArray(zoneItems)]]),
  );
}

export function getBlockId(block: BuilderBlock): string | null {
  return isRecord(block.props) && typeof block.props.id === "string" ? block.props.id : null;
}

function getBlockType(block: BuilderBlock): string {
  return typeof block.type === "string" ? block.type : "component";
}

function collectChildZoneKeys(zones: Record<string, BuilderBlock[]>, componentId: string): string[] {
  return Object.keys(zones)
    .filter((zoneKey) => zoneKey.startsWith(`${componentId}:`))
    .sort();
}

export function buildBlockLabel(block: BuilderBlock): string {
  const props = isRecord(block.props) ? block.props : {};
  const candidates = [
    props.label,
    props.title,
    props.heading,
    props.name,
    props.text,
  ];
  const named = candidates.find((value) => typeof value === "string" && value.trim().length > 0);
  if (typeof named === "string") {
    return named.trim();
  }

  return getBlockType(block);
}

function buildChildren(
  zones: Record<string, BuilderBlock[]>,
  componentId: string,
): LayerNode[] {
  const zoneKeys = collectChildZoneKeys(zones, componentId);
  const children: LayerNode[] = [];

  for (const zoneKey of zoneKeys) {
    const zoneName = zoneKey.slice(componentId.length + 1);
    for (const block of zones[zoneKey] ?? []) {
      const childId = getBlockId(block);
      if (!childId) continue;
      children.push({
        id: childId,
        label: `${buildBlockLabel(block)} · ${zoneName}`,
        type: getBlockType(block),
        children: buildChildren(zones, childId),
      });
    }
  }

  return children;
}

export function buildLayerNodes(data: Data): LayerNode[] {
  const zones = isRecord(data.zones)
    ? Object.fromEntries(
        Object.entries(data.zones).map(([zoneKey, zoneItems]) => [zoneKey, asBlockArray(zoneItems)]),
      ) as Record<string, BuilderBlock[]>
    : {};

  return asBlockArray(data.content).flatMap((block) => {
    const blockId = getBlockId(block);
    if (!blockId) return [];

    return [{
      id: blockId,
      label: buildBlockLabel(block),
      type: getBlockType(block),
      children: buildChildren(zones, blockId),
    }];
  });
}

function flattenLayerNodes(nodes: readonly LayerNode[]): BlockSummary[] {
  return nodes.flatMap((node) => [
    { id: node.id, type: node.type },
    ...flattenLayerNodes(node.children ?? []),
  ]);
}

export function collectBlockSummaries(data: Data): BlockSummary[] {
  return flattenLayerNodes(buildLayerNodes(data));
}

function findBlockLocation(data: Data, blockId: string): BlockLocation | null {
  const content = asBlockArray(data.content);
  const contentIndex = content.findIndex((block) => getBlockId(block) === blockId);
  if (contentIndex >= 0) {
    return {
      collection: "content",
      index: contentIndex,
      block: content[contentIndex]!,
    };
  }

  if (!isRecord(data.zones)) return null;

  for (const [zoneKey, zoneItems] of Object.entries(data.zones)) {
    const blocks = asBlockArray(zoneItems);
    const zoneIndex = blocks.findIndex((block) => getBlockId(block) === blockId);
    if (zoneIndex >= 0) {
      return {
        collection: "zone",
        zoneKey,
        index: zoneIndex,
        block: blocks[zoneIndex]!,
      };
    }
  }

  return null;
}

export function getBlockById(data: Data, blockId: string): BuilderBlock | null {
  return findBlockLocation(data, blockId)?.block ?? null;
}

export function updateBlockById(
  data: Data,
  blockId: string,
  updater: (block: BuilderBlock) => BuilderBlock,
): Data {
  const location = findBlockLocation(data, blockId);
  if (!location) return data;

  if (location.collection === "content") {
    const nextContent = [...asBlockArray(data.content)];
    nextContent[location.index] = updater(location.block);
    return {
      ...data,
      content: nextContent as Data["content"],
    };
  }

  const nextZones = cloneZones(data);
  const zoneKey = location.zoneKey!;
  const nextZoneItems = [...(nextZones[zoneKey] ?? [])];
  nextZoneItems[location.index] = updater(location.block);
  nextZones[zoneKey] = nextZoneItems;

  return {
    ...data,
    zones: nextZones as Data["zones"],
  };
}

function firstChildZoneKey(zones: Record<string, BuilderBlock[]>, blockId: string): string | null {
  const keys = collectChildZoneKeys(zones, blockId);
  return keys[0] ?? null;
}

export function reorderLayerData(
  data: Data,
  dragId: string,
  dropId: string,
  position: LayerPosition,
): Data {
  const dragLocation = findBlockLocation(data, dragId);
  const dropLocation = findBlockLocation(data, dropId);
  if (!dragLocation || !dropLocation || dragId === dropId) {
    return data;
  }

  const nextContent = [...asBlockArray(data.content)];
  const nextZones = cloneZones(data);

  const sourceItems = dragLocation.collection === "content"
    ? nextContent
    : nextZones[dragLocation.zoneKey!] ?? [];
  const [movedBlock] = sourceItems.splice(dragLocation.index, 1);
  if (!movedBlock) {
    return data;
  }

  if (position === "inside") {
    const zoneKey = firstChildZoneKey(nextZones, dropId);
    if (zoneKey) {
      nextZones[zoneKey] = [...(nextZones[zoneKey] ?? []), movedBlock];
      return {
        ...data,
        content: nextContent as Data["content"],
        zones: nextZones as Data["zones"],
      };
    }
  }

  const targetItems = dropLocation.collection === "content"
    ? nextContent
    : nextZones[dropLocation.zoneKey!] ?? [];
  const sameCollection = dragLocation.collection === dropLocation.collection
    && dragLocation.zoneKey === dropLocation.zoneKey;
  const adjustedIndex = sameCollection && dragLocation.index < dropLocation.index
    ? dropLocation.index - 1
    : dropLocation.index;
  const insertIndex = position === "before" ? adjustedIndex : adjustedIndex + 1;
  targetItems.splice(insertIndex, 0, movedBlock);

  return {
    ...data,
    content: nextContent as Data["content"],
    zones: Object.keys(nextZones).length > 0 ? nextZones as Data["zones"] : undefined,
  };
}
